import { z } from "zod";

export const JobSalaryDtoSchema = z.object({
  jobTitle: z.string(),
  areaTitle: z.string(),
  quantile: z.string(),
  salary: z.number(),
  salaryUp: z.boolean(),
  salaryDown: z.boolean(),
  netChange: z.number(),
  netChangeUp: z.boolean(),
  netChangeDown: z.boolean(),
  totalCostProjectionOneYear: z.number(),
  totalCostProjectionThreeYears: z.number(),
  totalCostProjectionFiveYears: z.number(),
  liquidity: z.number(),
  averageTenureInYears: z.number(),
  growthRate: z.number(),
  netChangeOneYear: z.number(),
  netChangeTwoYear: z.number(),
  netChangeThreeYear: z.number(),
  netChangeFourYear: z.number(),
  netChangeFiveYear: z.number(),
});

export type JobSalaryDto = z.infer<typeof JobSalaryDtoSchema>;

export const WatchlistJobSchema = z.object({
  jobTitle: z.string(),
  jobSubject: z.string(),
  jobTitleAliases: z.array(z.string()).default([]),
});

export type WatchlistJob = z.infer<typeof WatchlistJobSchema>;

export const WatchlistDtoSchema = z.object({
  id: z.string(),
  title: z.string(),
  groupName: z.string(),
  entries: z.array(WatchlistJobSchema),
});

export const WatchlistDtoListSchema = z.array(WatchlistDtoSchema);

export type WatchlistDto = z.infer<typeof WatchlistDtoSchema>;

export interface WatchlistGroup {
  id: string;
  title: string;
  watchlists: WatchlistDto[];
}

export const MetroAreaDtoSchema = z.object({
  areaId: z.string(),
  areaTitle: z.string(),
});

export const MetroAreaDtoListSchema = z.array(MetroAreaDtoSchema);

export const JobTitleToRelatedCareerSchema = z.object({
  jobTitle: z.string(),
  relatedCareers: z.array(z.string()),
});

export const JobTitleToRelatedCareerArraySchema = z.array(JobTitleToRelatedCareerSchema);

// Don't want graph to render missing data as zero so convert to NaN
// JSON standard doesn't allow for "NaN" values, so the server sends 0 instead
function convertZeroToNaN(value: number): number {
  return value === 0 ? NaN : value;
}

export const HistoricSalarySchema = z.object({
  time: z.number(),
  salary: z.number(), // For backwards compatibility, same as a_median
  a_pct10: z.number().transform(convertZeroToNaN),
  a_pct25: z.number().transform(convertZeroToNaN),
  a_median: z.number().transform(convertZeroToNaN),
  a_pct75: z.number().transform(convertZeroToNaN),
  a_pct90: z.number().transform(convertZeroToNaN),
});

export type HistoricSalary = z.infer<typeof HistoricSalarySchema>;

export const HistoricSalaryArraySchema = z.array(HistoricSalarySchema);

const ErrorResponseSchema = z.object({
  status: z.literal("error"),
  message: z.string(),
});

export const HistoricSalaryResponseSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("ok"), data: HistoricSalaryArraySchema }),
  ErrorResponseSchema,
]);
